/**
 * Acquisition log.
 *
 * The permanent record: every run ever started, what it was asked for, what it
 * cost, and exactly where it got to. Phase 4 shipped the live half - inline
 * progress and the pause/resume/cancel controls. Phase 5 adds the filters, the
 * paging and the route into one run's per-company trail.
 *
 * The filters live in the URL rather than in component state. That is what makes
 * the catalogue's "View log" link work: it is an ordinary link to
 * `/log?sicCode=78101`, not a piece of cross-screen message passing. It also
 * means a filtered log can be bookmarked and reloaded.
 *
 * Polling runs only while something is live, and stops the moment nothing is.
 *
 * Three ways to look at the same runs, picked from the .view-switch:
 *   - **Carded**   - the full AcquisitionProgress card, in a grid. Every counter
 *     visible for every run; the most space per run, the fewest runs on screen.
 *   - **List**     - one dense table row per run. The most runs visible at once;
 *     open a run to see more than its headline numbers.
 *   - **List + detail** - a master-detail split, same shape as the registry:
 *     compact rows on the left, the selected run's full card on the right. No
 *     navigation needed to go from scanning to reading one run closely.
 * The view choice is a screen preference, not a fact about the data, so it lives
 * in localStorage like the registry's page size - not in the URL. The selected
 * run in list + detail mode, like the registry's selected company, does live in
 * the URL (`?run=`), so a specific run's detail pane is bookmarkable.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AcquisitionProgress,
  STATUS_META,
  formatNumber,
  hasVisibleProgress,
  phaseCaption,
  progressBarTone,
} from '../components/AcquisitionProgress.jsx';
import DeleteSicCodeDialog from '../components/DeleteSicCodeDialog.jsx';
import {
  IconAlert,
  IconChevronRight,
  IconClose,
  IconClock,
  IconFilter,
  IconLayoutGrid,
  IconLayoutList,
  IconLayoutSidebar,
  IconLog,
  IconPause,
  IconPlay,
  IconSpinner,
  IconTrash,
} from '../components/Icons.jsx';
import { ScheduleDialog } from '../components/ScheduleDialog.jsx';
import { acquisitionApi } from '../lib/api.js';

const POLL_INTERVAL_MS = 4000;
const LIVE_STATUSES = ['queued', 'running'];
// The statuses `resume` accepts - mirrors AcquisitionProgress's own footer logic,
// since the List view's per-row and bulk resume must offer it in the same cases
// the full card does.
const RESUMABLE_STATUSES = ['paused', 'failed', 'cancelled'];
const PAGE_SIZE = 20;
const VIEW_KEY = 'leads-scanner.log.view';

/** Only the statuses a run can actually reach, in the order they happen in. */
const STATUS_FILTERS = [
  { value: 'running', label: 'Running' },
  { value: 'queued', label: 'Queued' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Incomplete' },
  { value: 'cancelled', label: 'Cancelled' },
];

const VIEW_OPTIONS = [
  {
    value: 'cards',
    label: 'Carded',
    Icon: IconLayoutGrid,
    tip: 'Every run as a full card, all its counters at a glance.',
  },
  {
    value: 'list',
    label: 'List',
    Icon: IconLayoutList,
    tip: 'One dense row per run - the most runs visible at once.',
  },
  {
    value: 'detail',
    label: 'List + detail',
    Icon: IconLayoutSidebar,
    tip: "A compact list with the selected run's full card alongside it.",
  },
];

function readView() {
  try {
    const stored = window.localStorage.getItem(VIEW_KEY);
    return VIEW_OPTIONS.some((entry) => entry.value === stored) ? stored : 'cards';
  } catch {
    return 'cards';
  }
}

export default function LogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(readView);
  const [showSchedule, setShowSchedule] = useState(false);

  const [page, setPage] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);
  // Which code the delete-category dialog is open for, and its description
  // (for the dialog's copy) - null when closed. Set either from the filter
  // bar's trash icon (scoped to the active SIC code filter) or from a
  // per-run delete button (scoped to that run's own code), so one dialog
  // serves both entry points.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCategoryMessage, setDeleteCategoryMessage] = useState(null);
  // Checked run uuids in the List view, and which uuids currently have a
  // pause/resume request in flight - both are screen state, not URL state,
  // the same way the view choice itself is.
  const [selected, setSelected] = useState(() => new Set());
  const [rowBusy, setRowBusy] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const timerRef = useRef(null);

  const sicCode = searchParams.get('sicCode') ?? '';
  const status = searchParams.get('status') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const offset = Number(searchParams.get('offset') ?? 0);
  const selectedRun = searchParams.get('run') ?? '';

  const hasFilters = Boolean(sicCode || status || from || to);

  /**
   * Writes one filter into the URL. Changing an actual filter resets to the
   * first page - staying on page 5 of a result set that now has two pages
   * shows an empty list and looks like a bug. Paging itself, and picking a run
   * to view in list + detail mode, must not reset each other.
   */
  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'offset' && key !== 'run') next.delete('offset');
    setSearchParams(next, { replace: true });
  };

  /** Opens the delete-category dialog scoped to one run's own SIC code -
   *  shared by the Carded, List and List + detail views' per-run delete
   *  buttons. */
  const openDeleteForRun = (run) => setDeleteTarget({ sicCode: run.sicCode, description: run.sicDescription });

  /** Same eligibility AcquisitionProgress's own footer uses - a run only
   *  offers the action its current status can actually take. */
  const canPause = (run) => LIVE_STATUSES.includes(run.status);
  const canResume = (run) => RESUMABLE_STATUSES.includes(run.status);

  const toggleSelected = (uuid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!runs?.length) return;
    setSelected((prev) => (runs.every((run) => prev.has(run.uuid)) ? new Set() : new Set(runs.map((run) => run.uuid))));
  };

  /** One run's pause/resume, from its own row button. Mirrors
   *  AcquisitionProgress.act() - a failed request just falls back to
   *  reloading the list, since the run's own status is the truth either way. */
  const runAction = async (uuid, action) => {
    setRowBusy((prev) => new Set(prev).add(uuid));
    try {
      await acquisitionApi[action](uuid);
    } catch {
      // Fall through to the reload below either way - it will show whatever
      // the run's status actually ended up as.
    } finally {
      await load();
      setRowBusy((prev) => {
        const next = new Set(prev);
        next.delete(uuid);
        return next;
      });
    }
  };

  /** Pause or resume every checked run that is currently eligible for it.
   *  Runs that are selected but not eligible (e.g. a completed run swept up
   *  in a "select all") are silently skipped rather than erroring - the
   *  button's own count already tells the operator how many will actually
   *  move. */
  const bulkAction = async (action, eligible) => {
    const targets = (runs ?? []).filter((run) => selected.has(run.uuid) && eligible(run));
    if (targets.length === 0) return;
    setBulkBusy(true);
    try {
      await Promise.allSettled(targets.map((run) => acquisitionApi[action](run.uuid)));
    } finally {
      await load();
      setBulkBusy(false);
    }
  };

  const chooseView = (value) => {
    setView(value);
    try {
      window.localStorage.setItem(VIEW_KEY, value);
    } catch {
      // Private browsing, or storage full - the choice still applies to this
      // session; only the memory of it is lost.
    }
  };

  const load = useCallback(async () => {
    try {
      const result = await acquisitionApi.list({
        limit: PAGE_SIZE,
        offset,
        sicCode: sicCode || undefined,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setPage(result);
      setError(null);
    } catch (caught) {
      setError(caught.message ?? 'Could not load the acquisition log.');
    }
  }, [offset, sicCode, status, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  // A checked run that scrolls off the page - a filter change, a page turn -
  // must not stay silently selected: "3 selected" acting on rows the operator
  // can no longer see would be a bulk action performed blind.
  useEffect(() => {
    setSelected(new Set());
  }, [sicCode, status, from, to, offset]);

  // The filter controls describe the log, not the catalogue, so they are loaded
  // once and left alone - a new run does not add a SIC code the operator cannot
  // already see in the list.
  useEffect(() => {
    acquisitionApi
      .filterOptions()
      .then(setOptions)
      .catch(() => setOptions(null));
  }, []);

  const runs = page?.runs ?? null;
  const total = page?.total ?? 0;
  const anyLive = (runs ?? []).some((run) => LIVE_STATUSES.includes(run.status));

  // The list itself only needs polling while something is live; each live run
  // polls itself at 2s for its own counters.
  useEffect(() => {
    if (!anyLive) {
      if (timerRef.current) clearInterval(timerRef.current);
      return undefined;
    }
    timerRef.current = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [anyLive, load]);

  const rateLimit = page?.rateLimit ?? null;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + PAGE_SIZE, total);
  const selectedRunData = runs?.find((run) => run.uuid === selectedRun) ?? null;

  const liveRuns = (runs ?? []).filter((r) => r.status === 'running');
  const queuedRuns = (runs ?? []).filter((r) => r.status === 'queued');
  const scheduledRuns = (runs ?? []).filter((r) => r.status === 'scheduled');

  const autoAcquireBtn = (
    <button
      type="button"
      className="btn btn-sm tip tip-below tip-left"
      onClick={() => setShowSchedule(true)}
      data-tip="Build a queue of SIC codes that run one after another, now or at a future time."
    >
      <IconClock size={14} />
      Auto Acquire
    </button>
  );

  const queueBanner =
    liveRuns.length > 0 || queuedRuns.length > 0 || scheduledRuns.length > 0 ? (
      <div className="queue-banner">
        {liveRuns.length > 0 ? (
          <span className="queue-banner-item">
            <IconSpinner />
            <span>
              <strong>{liveRuns[0].sicCode}</strong> running
            </span>
          </span>
        ) : null}
        {queuedRuns.length > 0 ? (
          <span className="queue-banner-item">
            <IconPlay size={12} />
            <span>
              {queuedRuns.length} queued
            </span>
          </span>
        ) : null}
        {scheduledRuns.length > 0 ? (
          <span className="queue-banner-item">
            <IconClock size={12} />
            <span>
              {scheduledRuns.length} scheduled
            </span>
          </span>
        ) : null}
      </div>
    ) : null;

  const viewSwitch = (
    <div className="view-switch" role="group" aria-label="Run list layout">
      {VIEW_OPTIONS.map((entry) => (
        <button
          key={entry.value}
          type="button"
          className={`view-switch-btn${view === entry.value ? ' is-active' : ''} tip tip-below`}
          onClick={() => chooseView(entry.value)}
          aria-pressed={view === entry.value}
          data-tip={entry.tip}
        >
          <entry.Icon size={14} />
          {entry.label}
        </button>
      ))}
    </div>
  );

  const filterControls = (
    <div className="filter-bar filter-bar-tight">
      <label className="filter-control">
        <span className="filter-label">SIC code</span>
        <span className="row row-tight">
          <select className="select" value={sicCode} onChange={(event) => setFilter('sicCode', event.target.value)}>
            {/* Only codes that have actually been acquired: a dropdown of all
                731 when four have been run is a worse control than one of four. */}
            <option value="">Every code</option>
            {(options?.sicCodes ?? []).map((entry) => (
              <option key={entry.sicCode} value={entry.sicCode}>
                {entry.sicCode} — {entry.description} ({entry.runCount})
              </option>
            ))}
          </select>
          {sicCode ? (
            <button
              type="button"
              className="btn btn-ghost btn-icon btn-sm tip tip-below tip-left"
              onClick={() =>
                setDeleteTarget({
                  sicCode,
                  description: options?.sicCodes?.find((entry) => entry.sicCode === sicCode)?.description,
                })
              }
              data-tip={`Permanently delete every company and run for ${sicCode} from the registry and this log.`}
              aria-label={`Delete every company and run for ${sicCode}`}
            >
              <IconTrash />
            </button>
          ) : null}
        </span>
      </label>

      <label className="filter-control">
        <span className="filter-label">Status</span>
        <select className="select" value={status} onChange={(event) => setFilter('status', event.target.value)}>
          <option value="">Any status</option>
          {STATUS_FILTERS.map((entry) => {
            const count = options?.statuses?.find((row) => row.status === entry.value)?.runCount;
            return (
              <option key={entry.value} value={entry.value}>
                {entry.label}
                {count ? ` (${count})` : ''}
              </option>
            );
          })}
        </select>
      </label>

      <label className="filter-control">
        <span className="filter-label">Started from</span>
        <input
          className="input"
          type="date"
          value={from}
          max={to || undefined}
          onChange={(event) => setFilter('from', event.target.value)}
        />
      </label>

      <label className="filter-control">
        <span className="filter-label">Started to</span>
        <input
          className="input"
          type="date"
          value={to}
          min={from || undefined}
          onChange={(event) => setFilter('to', event.target.value)}
        />
      </label>
    </div>
  );

  /**
   * Runs the bulk delete's dialog hands back, whichever of the three entry
   * points opened it (the filter bar's trash icon, a Carded run's own, or a
   * List row's own - all scoped to whatever SIC code they were opened for,
   * not necessarily the active filter). The deleted runs can no longer be
   * shown, so a SIC code filter or selected run that pointed at the deleted
   * code is cleared - but one that pointed somewhere else is left alone,
   * since a delete triggered from a row must not silently drop an unrelated
   * filter. The run list and the filter-option counts are refetched either
   * way - the same two things a filter change or a reclassify already
   * trigger elsewhere in the app.
   */
  const handleCategoryDeleted = ({ companies, runs: deletedRunCount, sicCode: deletedCode }) => {
    setDeleteTarget(null);
    setDeleteCategoryMessage(
      `Deleted ${companies.toLocaleString()} compan${companies === 1 ? 'y' : 'ies'} and ` +
        `${deletedRunCount.toLocaleString()} acquisition run${deletedRunCount === 1 ? '' : 's'} for ${deletedCode}.`,
    );

    const next = new URLSearchParams(searchParams);
    let paramsChanged = false;
    if (sicCode === deletedCode) {
      next.delete('sicCode');
      next.delete('offset');
      paramsChanged = true;
    }
    if (selectedRunData?.sicCode === deletedCode) {
      next.delete('run');
      paramsChanged = true;
    }
    if (paramsChanged) {
      // `load` is a useCallback keyed on the URL params, and the effect that
      // calls it re-runs the moment those params change - the same way
      // picking a different filter already refreshes the list, so no
      // separate call is needed here.
      setSearchParams(next, { replace: true });
    } else {
      void load();
    }

    acquisitionApi
      .filterOptions()
      .then(setOptions)
      .catch(() => setOptions(null));
  };

  /** The same status badge AcquisitionProgress renders, for the compact
   *  List and List + detail rows - a run must not look "Completed" in the
   *  full card and something else in the row that links to it. */
  const statusMeta = (run) => STATUS_META[run.status] ?? { label: run.status, tone: '' };

  /** The same "done vs outstanding" bar the full card shows, sized for a
   *  table cell or a compact row - null once a run has cleanly finished,
   *  since there is nothing left outstanding to show. The count is printed
   *  next to the bar, not left to a hover tooltip - a number you have to
   *  hover for is not a number you can scan a list of. */
  const miniProgress = (run) =>
    hasVisibleProgress(run) ? (
      <span className="row row-tight">
        <div
          className="progress progress-mini tip"
          data-tip={`${formatNumber(run.progressCount ?? run.companiesFound)} of about ${formatNumber(run.totalHits)} companies ${run.inProfilePhase ? 'profiled' : 'found'} so far.`}
        >
          <div
            className={`progress-bar ${progressBarTone(run)}`}
            style={{ width: `${Math.min(100, run.percent ?? 0)}%` }}
          />
        </div>
        <span className="mono">
          {formatNumber(run.progressCount ?? run.companiesFound)}/{formatNumber(run.totalHits)}
        </span>
      </span>
    ) : null;

  if (view === 'detail') {
    return (
      <div className="master-detail">
        <div className="master">
          <div className="master-toolbar">
            <div className="row row-between">
              <span className="card-title">Runs</span>
              <div className="row row-tight">
                {rateLimit ? (
                  <span
                    className="rate-chip-inline tip tip-below tip-left"
                    data-tip={`Companies House allows 600 requests per 5 minutes for the whole application. This app spends at most ${rateLimit.limit}, and background runs stop at ${rateLimit.backgroundCeiling} so interactive work always has room.`}
                  >
                    <span className="rate-chip-inline-value">
                      {rateLimit.used} / {rateLimit.limit}
                    </span>
                    <span className="rate-chip-inline-label">API calls this window</span>
                  </span>
                ) : null}
                {autoAcquireBtn}
                {viewSwitch}
              </div>
            </div>
            {queueBanner}
            <p className="text-xs text-dim">
              Every run ever started, newest first. Pick one to see its full card on the right.
            </p>

            {filterControls}

            {deleteCategoryMessage ? <p className="text-xs text-dim">{deleteCategoryMessage}</p> : null}

            {hasFilters ? (
              <div className="row row-between">
                <span className="text-xs text-dim">
                  {total.toLocaleString()} run{total === 1 ? '' : 's'} match
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const next = new URLSearchParams();
                    if (selectedRun) next.set('run', selectedRun);
                    setSearchParams(next, { replace: true });
                  }}
                >
                  <IconClose />
                  Clear filters
                </button>
              </div>
            ) : null}
          </div>

          {error ? (
            <div className="master-list">
              <div className="alert alert-error">
                <span className="alert-icon">
                  <IconAlert />
                </span>
                <span className="alert-body">
                  <span className="alert-title">Could not load the log</span>
                  {error}
                </span>
              </div>
            </div>
          ) : runs === null ? (
            <div className="master-list">
              <div className="loading-panel">
                <span className="spinner" />
                Loading the log
              </div>
            </div>
          ) : runs.length === 0 ? (
            <div className="master-list">
              <div className="empty">
                <span className="empty-icon">
                  <IconLog />
                </span>
                <h3 className="empty-title">
                  {hasFilters ? 'No runs match these filters' : 'No acquisitions have run yet'}
                </h3>
                <p className="empty-description">
                  {hasFilters
                    ? 'Widen the dates or press Clear filters to see everything.'
                    : 'Go to the Catalogue and press Acquire on a SIC code to start one.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="master-list">
              {runs.map((run) => {
                const meta = statusMeta(run);
                return (
                  <button
                    key={run.uuid}
                    type="button"
                    className={`master-item${selectedRun === run.uuid ? ' is-selected' : ''}`}
                    onClick={() => setFilter('run', run.uuid)}
                  >
                    <span className="master-item-title">
                      <span className="mono">{run.sicCode}</span> {run.sicDescription}
                    </span>
                    <span className="master-item-meta">
                      <span className={`badge badge-sm ${meta.tone}`}>
                        {run.status === 'running' ? <IconSpinner /> : null}
                        {meta.label}
                        {phaseCaption(run) ? ` · ${phaseCaption(run)}` : ''}
                      </span>
                      {/* A live or gap-carrying run shows its progress fraction here instead
                          of the date - that is the number worth scanning for while it is
                          still moving. The date is still one click away via Open run. */}
                      {miniProgress(run) ?? (
                        <>
                          <span>{formatNumber(run.companiesFound)} found</span>
                          {run.startedAt ? <span>{new Date(run.startedAt).toLocaleDateString()}</span> : null}
                        </>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="master-footer">
            <span>
              {formatNumber(showingFrom)}–{formatNumber(showingTo)} of {formatNumber(total)}
            </span>
            <span className="spacer" />
            {total > PAGE_SIZE ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={offset === 0}
                  onClick={() => setFilter('offset', String(Math.max(0, offset - PAGE_SIZE)))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={showingTo >= total}
                  onClick={() => setFilter('offset', String(offset + PAGE_SIZE))}
                >
                  Next
                </button>
              </>
            ) : null}
          </div>
        </div>

        <div className="detail">
          <div className="detail-body">
            {selectedRunData ? (
              <AcquisitionProgress
                key={selectedRunData.uuid}
                run={selectedRunData}
                onChanged={() => void load()}
                linkToDetail
                onDeleteCategory={openDeleteForRun}
              />
            ) : (
              <div className="empty">
                <h3 className="empty-title">Select a run to see its detail</h3>
                <p className="empty-description">
                  Choose one from the list on the left - its full card, counters and controls will
                  appear here.
                </p>
              </div>
            )}
          </div>
        </div>

        {deleteTarget ? (
          <DeleteSicCodeDialog
            sicCode={deleteTarget.sicCode}
            description={deleteTarget.description}
            onCancel={() => setDeleteTarget(null)}
            onDeleted={handleCategoryDeleted}
          />
        ) : null}
        {showSchedule ? (
          <ScheduleDialog onClose={() => { setShowSchedule(false); void load(); }} />
        ) : null}
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-content page-content-full">
        <div className="col" style={{ gap: 'var(--sp-5)' }}>
          {/* --- Filters, in their own window ------------------------- */}
          <div className="card">
            <div className="card-header">
              <span className="card-title row row-tight">
                <IconFilter size={14} />
                Filters
              </span>
              <div className="card-header-actions">
                {rateLimit ? (
                  <span
                    className="rate-chip-inline tip tip-below tip-left"
                    data-tip={`Companies House allows 600 requests per 5 minutes for the whole application. This app spends at most ${rateLimit.limit}, and background runs stop at ${rateLimit.backgroundCeiling} so interactive work always has room.`}
                  >
                    <span className="rate-chip-inline-value">
                      {rateLimit.used} / {rateLimit.limit}
                    </span>
                    <span className="rate-chip-inline-label">API calls this window</span>
                  </span>
                ) : null}
                {rateLimit && hasFilters ? <span className="header-divider" /> : null}
                {hasFilters ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm tip tip-below tip-left"
                    onClick={() => setSearchParams({}, { replace: true })}
                    data-tip="Clear every filter and show the whole log again."
                  >
                    <IconClose />
                    Clear filters
                  </button>
                ) : null}
              </div>
            </div>
            <div className="card-body">
              {filterControls}
              {deleteCategoryMessage ? (
                <p className="text-xs text-dim" style={{ marginTop: 'var(--sp-3)' }}>
                  {deleteCategoryMessage}
                </p>
              ) : null}
            </div>
          </div>

          {error ? (
            <div className="alert alert-error">
              <span className="alert-icon">
                <IconAlert />
              </span>
              <span className="alert-body">
                <span className="alert-title">Could not load the log</span>
                {error}
              </span>
            </div>
          ) : null}

          {/* --- Results, in their own window --------------------------- */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">
                Runs
                {queueBanner}
              </span>
              <div className="card-header-actions">
                {anyLive ? (
                  <span className="text-accent text-sm row row-tight">
                    <IconSpinner /> live, refreshing
                  </span>
                ) : null}
                {autoAcquireBtn}
                {viewSwitch}
              </div>
            </div>
            <div className="card-body col" style={{ gap: 'var(--sp-4)' }}>
              {runs === null ? (
                <div className="loading-panel">
                  <span className="spinner" />
                  Loading the log
                </div>
              ) : runs.length === 0 ? (
                <div className="empty">
                  <span className="empty-icon">
                    <IconLog />
                  </span>
                  <h2 className="empty-title">
                    {hasFilters ? 'No runs match these filters' : 'No acquisitions have run yet'}
                  </h2>
                  <p className="empty-description">
                    {hasFilters ? (
                      <>
                        The log holds runs, but none with this combination. Widen the dates or press{' '}
                        <strong>Clear</strong> to see everything.
                      </>
                    ) : (
                      <>
                        Go to the <strong>Catalogue</strong>, find the codes that describe your market, and
                        press <strong>Acquire</strong>. Every run will be listed here with live progress
                        while it works.
                      </>
                    )}
                  </p>
                </div>
              ) : (
                <>
                  {view === 'list' && selected.size > 0 ? (
                    <div className="bulk-bar">
                      <span className="text-sm">
                        {selected.size.toLocaleString()} selected
                      </span>
                      <div className="row row-tight">
                        <button
                          type="button"
                          className="btn btn-sm tip tip-below"
                          disabled={bulkBusy || !runs.some((run) => selected.has(run.uuid) && canPause(run))}
                          onClick={() => void bulkAction('pause', canPause)}
                          data-tip="Pauses every selected run that is queued or running. Others in the selection are left alone."
                        >
                          <IconPause />
                          Pause
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary tip tip-below"
                          disabled={bulkBusy || !runs.some((run) => selected.has(run.uuid) && canResume(run))}
                          onClick={() => void bulkAction('resume', canResume)}
                          data-tip="Resumes every selected run that is paused, incomplete or cancelled. Others in the selection are left alone."
                        >
                          <IconPlay />
                          Start
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>
                          <IconClose />
                          Clear
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="list-meta">
                      <span>
                        Showing {showingFrom.toLocaleString()}–{showingTo.toLocaleString()} of{' '}
                        {total.toLocaleString()} run{total === 1 ? '' : 's'}
                      </span>
                    </div>
                  )}

                  {view === 'list' ? (
                    <div className="table-wrap">
                      <table className="table">
                        <thead>
                          <tr>
                            <th scope="col" className="table-checkbox-col">
                              <input
                                type="checkbox"
                                className="checkbox"
                                checked={runs.length > 0 && runs.every((run) => selected.has(run.uuid))}
                                onChange={toggleSelectAll}
                                aria-label="Select all runs on this page"
                              />
                            </th>
                            <th scope="col">Code</th>
                            <th scope="col">Description</th>
                            <th scope="col">Status</th>
                            <th scope="col">Progress</th>
                            <th scope="col" className="table-num">
                              Found
                            </th>
                            <th scope="col" className="table-num">
                              New
                            </th>
                            <th scope="col" className="table-num">
                              Updated
                            </th>
                            <th scope="col">Started</th>
                            <th scope="col" aria-hidden="true"></th>
                            <th scope="col" aria-hidden="true"></th>
                            <th scope="col" aria-hidden="true"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {runs.map((run) => {
                            const meta = statusMeta(run);
                            return (
                              <tr key={run.uuid} className={selected.has(run.uuid) ? 'is-selected' : ''}>
                                <td className="table-checkbox-col">
                                  <input
                                    type="checkbox"
                                    className="checkbox"
                                    checked={selected.has(run.uuid)}
                                    onChange={() => toggleSelected(run.uuid)}
                                    aria-label={`Select run ${run.sicCode}`}
                                  />
                                </td>
                                <td className="mono table-nowrap">{run.sicCode}</td>
                                <td>
                                  <Link className="table-link" to={`/log/${run.uuid}`}>
                                    {run.sicDescription}
                                  </Link>
                                </td>
                                <td>
                                  <span className={`badge badge-sm ${meta.tone}`}>
                                    {run.status === 'running' ? <IconSpinner /> : null}
                                    {meta.label}
                                    {phaseCaption(run) ? ` · ${phaseCaption(run)}` : ''}
                                  </span>
                                </td>
                                <td>{miniProgress(run) ?? <span className="value-absent">—</span>}</td>
                                <td className="table-num mono">{formatNumber(run.companiesFound)}</td>
                                <td className="table-num mono">{formatNumber(run.companiesNew)}</td>
                                <td className="table-num mono">{formatNumber(run.companiesUpdated)}</td>
                                <td className="table-nowrap text-dim">
                                  {run.startedAt ? new Date(run.startedAt).toLocaleDateString() : '—'}
                                </td>
                                <td>
                                  {canPause(run) ? (
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-icon tip tip-left"
                                      onClick={() => void runAction(run.uuid, 'pause')}
                                      disabled={rowBusy.has(run.uuid)}
                                      data-tip="Stops within one page and keeps the checkpoint. Resuming carries on from the same position."
                                      aria-label={`Pause run ${run.sicCode}`}
                                    >
                                      <IconPause />
                                    </button>
                                  ) : canResume(run) ? (
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-icon tip tip-left"
                                      onClick={() => void runAction(run.uuid, 'resume')}
                                      disabled={rowBusy.has(run.uuid)}
                                      data-tip="Picks up from the last committed page - no duplicates, no gaps."
                                      aria-label={`Start run ${run.sicCode}`}
                                    >
                                      <IconPlay />
                                    </button>
                                  ) : null}
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-icon tip tip-left"
                                    onClick={() => openDeleteForRun(run)}
                                    data-tip={`Permanently delete every company and run for ${run.sicCode} from the registry and this log.`}
                                    aria-label={`Delete every company and run for ${run.sicCode}`}
                                  >
                                    <IconTrash />
                                  </button>
                                </td>
                                <td>
                                  <Link
                                    className="btn btn-ghost btn-icon tip tip-left"
                                    to={`/log/${run.uuid}`}
                                    data-tip="Open this run's full card and per-company trail."
                                    aria-label={`Open run ${run.sicCode}`}
                                  >
                                    <IconChevronRight />
                                  </Link>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="run-grid">
                      {runs.map((run) => (
                        <AcquisitionProgress
                          key={run.uuid}
                          run={run}
                          onChanged={() => void load()}
                          linkToDetail
                          compact
                          onDeleteCategory={openDeleteForRun}
                        />
                      ))}
                    </div>
                  )}

                  {/* Paging appears only when there is more than one page of it. */}
                  {total > PAGE_SIZE ? (
                    <div className="pager">
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={offset === 0}
                        onClick={() => setFilter('offset', String(Math.max(0, offset - PAGE_SIZE)))}
                      >
                        Previous
                      </button>
                      <span className="text-sm text-dim">
                        Page {Math.floor(offset / PAGE_SIZE) + 1} of {Math.ceil(total / PAGE_SIZE)}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={showingTo >= total}
                        onClick={() => setFilter('offset', String(offset + PAGE_SIZE))}
                      >
                        Next
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {deleteTarget ? (
        <DeleteSicCodeDialog
          sicCode={deleteTarget.sicCode}
          description={deleteTarget.description}
          onCancel={() => setDeleteTarget(null)}
          onDeleted={handleCategoryDeleted}
        />
      ) : null}
      {showSchedule ? (
        <ScheduleDialog onClose={() => { setShowSchedule(false); void load(); }} />
      ) : null}
    </div>
  );
}
